import { forwardRef } from 'react';

import {
  Box,
  Stack,
  StackProps,
  Tooltip,
  Typography,
  TypographyProps,
  alpha,
  styled,
} from '@mui/material';
import { BoxProps } from '@mui/system';

export const Container = styled(({ children, ...props }: StackProps & { small?: boolean }) => (
  <Stack {...props}>{children}</Stack>
))(() => ({
  background: '#FEFEFE',
  borderRadius: '14px',
  padding: '16px',
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
  gap: '2rem',
  flexDirection: 'row',
  alignItems: 'center',
  minWidth: 0,
  overflow: 'hidden',
}));

export const ImageWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isChild',
})<{ isChild?: boolean }>(({ theme, isChild }) => ({
  position: 'relative',
  borderRadius: '12px',
  overflow: 'hidden',
  flexShrink: 0,
  width: isChild ? '6rem' : '13.75rem',
  height: isChild ? '6rem' : '8.125rem',
  aspectRatio: isChild ? undefined : '73/43',

  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: 'auto',
    aspectRatio: '73/43',
  },
}));

export const ImagePlaceholder = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.grey[200],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& svg': {
    fontSize: '3rem',
    color: theme.palette.grey[500],
  },
}));

export const Image = styled('img', {
  shouldForwardProp: (propName: string) => propName !== 'isDefault',
})<{ isDefault: boolean }>(({ isDefault }) => ({
  objectFit: 'cover',
  cursor: isDefault ? 'default' : 'pointer',
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
}));

export const TitleContainer = styled(Stack, { shouldForwardProp: (prop) => prop !== 'isChild' })<{
  isChild?: boolean;
}>(({ theme, isChild }) => ({
  gap: '0.5rem',
  [theme.breakpoints.up('lg')]: {
    padding: '0.25rem',
    height: isChild ? '6rem' : undefined,
  },
  [theme.breakpoints.down('lg')]: {
    padding: '1rem 0',
  },
  cursor: 'pointer',
  border: 'none',
  transition: 'background-color 0.3s ease, border 0.3s ease',

  ':hover': {
    backgroundColor: alpha(theme.palette.purple[50], 0.4),
  },
}));

const TitleComponent = forwardRef<HTMLElement, TypographyProps & { isChild?: boolean }>(
  ({ children, ...props }, ref) => (
    <Typography ref={ref} {...props}>
      {children}
    </Typography>
  ),
);
TitleComponent.displayName = 'TitleComponent';

export const Title = styled(TitleComponent, {
  shouldForwardProp: (prop) => prop !== 'isChild',
})<{ isChild?: boolean }>(({ isChild }) => ({
  width: '15rem',
  maxHeight: isChild ? '2.1rem' : '3rem',
  fontSize: isChild ? '0.875rem' : '1.125rem',
  fontWeight: 500,
  lineHeight: '120%',
  color: '#504E5A',
  margin: 0,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const TrafficValue = styled(({ children, ...props }: TypographyProps) => (
  <Typography {...props}>{children}</Typography>
))({
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: '160%',
  color: '#737085',
  margin: 0,
});

export const Specification = styled(
  ({ children, darker, ...props }: StackProps & { darker?: boolean }) => (
    <Stack data-darker={darker || undefined} {...props}>
      {children}
    </Stack>
  ),
)({
  position: 'relative',
  backgroundColor: '#F5F5F7',
  '&[data-darker]': {
    backgroundColor: '#EBEBEE',
  },
  borderRadius: '13px',
  padding: '4px 8px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '2px',
  width: '174px',
  flex: '0 0 174px',
  overflow: 'hidden',
});

export const SpecificationTitle = styled(({ children, ...props }: TypographyProps) => (
  <Typography {...props}>{children}</Typography>
))(({ theme }) => ({
  fontSize: '0.625rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  color: theme.palette.grey[600],
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const SpecificationValue = styled(
  ({ children, noTooltip, ...props }: BoxProps & { noTooltip?: boolean }) => (
    <Tooltip title={noTooltip ? undefined : children} placement="top" arrow>
      <Box {...props}>{children}</Box>
    </Tooltip>
  ),
)(() => ({
  fontSize: '0.625rem',
  fontWeight: 400,
  lineHeight: '160%',
  color: '#615E6F',
  margin: 0,
  minHeight: '1rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
  maxWidth: '100%',
}));

const SupportNameIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M12.8001 2.18424C12.8001 1.50575 12.2835 1 11.6999 1H2.10026C1.5166 1 1 1.50575 1 2.18424V11.6159C1.00005 12.2943 1.51663 12.8001 2.10026 12.8001H11.6999C12.8835 12.8001 12.8001 12.2943 12.8001 11.6159V2.18424ZM8.25065 4.13346C8.4238 4.13946 8.58136 4.23499 8.66732 4.38542L11.334 9.05208C11.4223 9.20675 11.422 9.39669 11.3327 9.55078C11.2432 9.70494 11.078 9.80013 10.8997 9.80013H2.89974C2.71544 9.80003 2.54595 9.69831 2.45898 9.53581C2.37209 9.37334 2.38163 9.17614 2.48372 9.02279L4.48372 6.02279L4.51758 5.97786C4.60104 5.87875 4.72052 5.81499 4.85091 5.80208C4.9999 5.78739 5.14738 5.84076 5.25325 5.94661L6.15495 6.84831L7.81706 4.35612L7.85612 4.30469C7.95426 4.19199 8.09905 4.12824 8.25065 4.13346ZM6.64909 7.91081C6.56598 8.03529 6.43151 8.11611 6.28255 8.13086C6.1335 8.1456 5.98547 8.09289 5.87956 7.98698L4.97721 7.08463L3.83398 8.80013H10.0384L8.20052 5.58333L6.64909 7.91081ZM13.8001 11.6159C13.8001 12.7977 12.8835 13.8001 11.6999 13.8001H2.10026C0.916638 13.8001 5.06091e-05 12.7977 0 11.6159V2.18424C0 1.00241 0.916606 0 2.10026 0H11.6999C12.8835 0 13.8001 1.00241 13.8001 2.18424V11.6159Z"
      fill="currentColor"
    />
  </svg>
);

export const SupportName = styled(({ children, ...props }: StackProps) => (
  <Stack {...props} direction="row" alignItems="center">
    <SupportNameIcon />
    <p>{children}</p>
  </Stack>
))({
  borderRadius: '8px',
  gap: '6px',
  padding: '4px 8px',
  alignSelf: 'flex-start',

  '& > p': {
    margin: 0,
    fontFamily: 'Neue Frutiger World',
    fontSize: '10px',
    fontWeight: 400,
    lineHeight: '160%',
    color: 'inherit',
  },
});

export const PricesStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '16px',
  padding: '0rem 1rem',
  [theme.breakpoints.up('md')]: {
    minWidth: '6rem',
  },

  [theme.breakpoints.up('lg')]: {
    minWidth: '8rem',
  },

  [theme.breakpoints.up('xl')]: {
    minWidth: '10rem',
  },
}));

export const PriceLabel = styled(({ children, ...props }: TypographyProps) => (
  <Typography {...props}>{children}</Typography>
))({
  margin: 0,
  color: '#807D93',
  fontFamily: 'Neue Frutiger World',
  fontSize: '10px',
  fontWeight: 400,
  lineHeight: '160%',
  alignSelf: 'stretch',
  textAlign: 'center',
});

export const OldPriceValue = styled(({ children, ...props }: TypographyProps) => (
  <Typography {...props}>{children}</Typography>
))(({ theme }) => ({
  margin: 0,
  color: theme.palette.grey[500],
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '130%',
  textDecorationLine: 'line-through',
  textAlign: 'center',
}));
export const PriceValue = styled(({ children, ...props }: TypographyProps) => (
  <Typography {...props}>{children}</Typography>
))({
  margin: 0,
  color: '#504E5A',
  fontFamily: 'Neue Frutiger World',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '130%',
  alignSelf: 'stretch',
  textAlign: 'center',
});
